import React from "react";
import { GeneratedPostsData, DraftPost } from "../types";
import { ConnectedProfile } from "../../../../types/customer";
import type { SingleCardBusy } from "../hooks/usePostActions";
import { SocialPostsPanel } from "./SocialPostsPanel";

/* ── Shimmer skeleton for canvas loading state ── */
const CanvasLoadingSkeleton: React.FC = () => (
  <div className="flex flex-col items-center gap-6 px-6 py-10 animate-pulse">
    {/* Status message */}
    <div className="flex items-center gap-3 rounded-full bg-[#f1f3f5] px-5 py-2.5 shadow-sm">
      <div className="h-5 w-5 rounded-full border-2 border-[#749B8F]/30 border-t-[#749B8F] animate-spin" />
      <span className="text-[13px] font-semibold text-[#718096]">
        Generating posts…
      </span>
    </div>

    {/* Skeleton cards */}
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="w-full max-w-[460px] rounded-2xl border border-theme-border bg-white p-5 shadow-sm"
        style={{ opacity: 1 - i * 0.2 }}
      >
        {/* Profile row */}
        <div className="mb-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[#e2e8f0]" />
          <div className="flex flex-col gap-1.5">
            <div className="h-3 w-28 rounded-full bg-[#e2e8f0]" />
            <div className="h-2.5 w-20 rounded-full bg-[#edf2f7]" />
          </div>
        </div>
        {/* Content lines */}
        <div className="mb-4 flex flex-col gap-2">
          <div className="h-3 w-full rounded-full bg-[#edf2f7]" />
          <div className="h-3 w-5/6 rounded-full bg-[#edf2f7]" />
          <div className="h-3 w-3/4 rounded-full bg-[#edf2f7]" />
        </div>
        {/* Image placeholder */}
        <div className="h-40 w-full rounded-xl bg-[#f1f3f5]" />
      </div>
    ))}
  </div>
);

type SocialPostsRightPanelProps = {
  surfaceBg: string;
  headerBar: string;
  panelSocialPosts: GeneratedPostsData | null;
  isLoading?: boolean;
  onSaveSingleDraft: (post: DraftPost) => void | Promise<void>;
  onPublishSingleDraft: (post: DraftPost) => void | Promise<void>;
  onScheduleSingleDraft: (post: DraftPost) => void | Promise<void>;
  singleCardBusy: SingleCardBusy | null;
  onInsertImageFromLibrary: (
    tempId: string,
    mediaType?: "image" | "video"
  ) => void | Promise<void>;
  onInsertCarouselFromLibrary?: (tempId: string, platform?: string) => void | Promise<void>;
  /** LinkedIn document posts (SGL-174). */
  onInsertDocumentFromLibrary?: (tempId: string) => void | Promise<void>;
  onInsertImageFromLocalFile: (tempId: string) => void;
  onClose: () => void;
  /** When set, the panel scrolls this post into view on mount. */
  focusTempId?: string | null;
  hasConnectedAccounts?: boolean;
  connectedProfiles?: ConnectedProfile[];
};

export const SocialPostsRightPanel: React.FC<SocialPostsRightPanelProps> = ({
  panelSocialPosts,
  isLoading = false,
  onSaveSingleDraft,
  onPublishSingleDraft,
  onScheduleSingleDraft,
  singleCardBusy,
  onInsertImageFromLibrary,
  onInsertCarouselFromLibrary,
  onInsertDocumentFromLibrary,
  onInsertImageFromLocalFile,
  onClose,
  focusTempId,
  hasConnectedAccounts,
  connectedProfiles = [],
}) => {
  const postCount = panelSocialPosts?.posts?.length ?? 0;
  const showSkeleton = isLoading && postCount === 0;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-theme-border bg-theme-surface">
      <header className="relative flex-shrink-0 border-b border-theme-border px-6 pb-4 pt-6">
        {/* Subtle gradient accent stripe */}
        <div className={`pointer-events-none absolute inset-x-0 top-0 h-[3px] ${
          showSkeleton
            ? "bg-gradient-to-r from-theme-primary/40 via-theme-primary to-theme-primary/40 animate-pulse"
            : "bg-gradient-to-r from-theme-border via-theme-primary/25 to-transparent"
        }`} />
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-sans text-xl font-bold tracking-tight text-theme-fg">
                Social Posts
              </h2>
              {postCount > 0 && (
                <span className="inline-flex shrink-0 items-center rounded-full bg-theme-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-theme-primary">
                  {postCount} {postCount === 1 ? "POST" : "POSTS"}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs font-medium text-theme-fg-muted">
              {showSkeleton ? "Creating your content…" : "Preview · edit · save, schedule, or publish each card"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-theme-fg-muted transition-colors hover:bg-theme-background hover:text-theme-fg"
            title="Close panel"
            aria-label="Close social posts panel"
          >
            <span className="material-icons text-[22px]">close</span>
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 flex flex-col">
        {panelSocialPosts?.posts?.length ? (
          <SocialPostsPanel
            posts={panelSocialPosts.posts}
            onSaveSingleDraft={onSaveSingleDraft}
            onPublishSingleDraft={onPublishSingleDraft}
            onScheduleSingleDraft={onScheduleSingleDraft}
            singleCardBusy={singleCardBusy}
            onInsertImageFromLibrary={onInsertImageFromLibrary}
            onInsertCarouselFromLibrary={onInsertCarouselFromLibrary}
            onInsertDocumentFromLibrary={onInsertDocumentFromLibrary}
            onInsertImageFromLocalFile={onInsertImageFromLocalFile}
            focusTempId={focusTempId ?? undefined}
            hasConnectedAccounts={hasConnectedAccounts}
            connectedProfiles={connectedProfiles}
          />
        ) : showSkeleton ? (
          <CanvasLoadingSkeleton />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-20 text-center">
            <span className="material-icons text-5xl text-theme-fg-muted opacity-30">
              article
            </span>
            <p className="text-sm font-medium text-theme-fg-muted opacity-50">
              No posts yet — generate posts in the chat
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
